import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, MapPin, Home, Globe, MessageSquare, Loader2 } from 'lucide-react';

const ClientRegistrationModal = ({ 
  isOpen, 
  onClose, 
  onRegister, 
  isRegistering, 
  customerDetails, 
  setCustomerDetails 
}) => {
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'postalCode', 'country'];
  const isFormValid = requiredFields.every(field => Boolean(customerDetails?.[field] && customerDetails[field].toString().trim() !== ''));

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors = {};
    if (!customerDetails.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!customerDetails.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!customerDetails.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!customerDetails.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!customerDetails.address?.trim()) newErrors.address = 'Street address is required';
    if (!customerDetails.city?.trim()) newErrors.city = 'City is required';
    if (!customerDetails.state?.trim()) newErrors.state = 'State is required';
    if (!customerDetails.postalCode?.trim()) newErrors.postalCode = 'Postal code is required';
    if (!customerDetails.country?.trim()) newErrors.country = 'Country is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onRegister(customerDetails);
  };

  const handleChange = (field, value) => {
    setCustomerDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full transform transition-all duration-300 scale-100 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <User className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Register New Client</h3>
              <p className="text-sm text-slate-300 mt-1">Add a new client to your database</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            disabled={isRegistering}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Personal Information Section */}
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-slate-600" />
                Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                    {errors.firstName && (
                      <span className="text-red-500 text-xs ml-2">{errors.firstName}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={customerDetails.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.firstName ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="John"
                      disabled={isRegistering}
                    />
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                    {errors.lastName && (
                      <span className="text-red-500 text-xs ml-2">{errors.lastName}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={customerDetails.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.lastName ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="Doe"
                      disabled={isRegistering}
                    />
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-slate-600" />
                Contact Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                    {errors.email && (
                      <span className="text-red-500 text-xs ml-2">{errors.email}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={customerDetails.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.email ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="john.doe@example.com"
                      disabled={isRegistering}
                    />
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                    {errors.phone && (
                      <span className="text-red-500 text-xs ml-2">{errors.phone}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      required
                      value={customerDetails.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.phone ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="+1 (555) 123-4567"
                      disabled={isRegistering}
                    />
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <Home className="h-5 w-5 mr-2 text-slate-600" />
                Address Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                    {errors.address && (
                      <span className="text-red-500 text-xs ml-2">{errors.address}</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={customerDetails.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.address ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="123 Main Street"
                      disabled={isRegistering}
                    />
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      City <span className="text-red-500">*</span>
                      {errors.city && (
                        <span className="text-red-500 text-xs ml-2">{errors.city}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required
                      value={customerDetails.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.city ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="New York"
                      disabled={isRegistering}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      State/Province <span className="text-red-500">*</span>
                      {errors.state && (
                        <span className="text-red-500 text-xs ml-2">{errors.state}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required
                      value={customerDetails.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.state ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="NY"
                      disabled={isRegistering}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Postal Code <span className="text-red-500">*</span>
                      {errors.postalCode && (
                        <span className="text-red-500 text-xs ml-2">{errors.postalCode}</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required
                      value={customerDetails.postalCode}
                      onChange={(e) => handleChange('postalCode', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.postalCode ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="10001"
                      disabled={isRegistering}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Country <span className="text-red-500">*</span>
                      {errors.country && (
                        <span className="text-red-500 text-xs ml-2">{errors.country}</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={customerDetails.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        className={`w-full px-4 py-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          errors.country ? 'border-red-300' : 'border-slate-300'
                        }`}
                        placeholder="United States"
                        disabled={isRegistering}
                      />
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-slate-600" />
                Additional Information
              </h4>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Special Requests & Preferences
                </label>
                <textarea
                  value={customerDetails.specialRequests}
                  onChange={(e) => handleChange('specialRequests', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Any special preferences, dietary requirements, accessibility needs, or other notes..."
                  disabled={isRegistering}
                />
                <p className="text-xs text-slate-500 mt-2">
                  This information will help us provide better service to your client
                </p>
              </div>
            </div>

            {/* Required Fields Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 flex items-start">
                <span className="text-blue-600 mr-2">*</span>
                <span>Fields marked with an asterisk (*) are required</span>
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={handleClose}
            disabled={isRegistering}
            className="flex-1 px-4 py-3 text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 hover:border-slate-400 rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isRegistering || !isFormValid}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {isRegistering ? (
              <div className="flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span>Registering...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <User className="h-5 w-5 mr-2" />
                <span>Register Client</span>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClientRegistrationModal;